if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        // ===== BUNDLE: Check if bundle components exist on this page =====
        const bundleWrapper = document.getElementById('bundle-components');
        const bundleItems = [];

        if (bundleWrapper) {
          const bundleId = `bundle-${Date.now()}`;

          // Get main product variant ID and quantity from the form
          const mainVariantId = this.form.querySelector('[name="id"]').value;
          const mainQty = parseInt(this.form.querySelector('[name="quantity"]')?.value || 1);

          // Push main product as first line item
          bundleItems.push({
            id: parseInt(mainVariantId),
            quantity: mainQty,
            properties: {
              _bundle_id: bundleId,
              _bundle_role: 'main'
            }
          });

          // Validate and collect <select> dropdowns (multi-variant components)
          let hasError = false;

          bundleWrapper.querySelectorAll('.bundle-component__variant-select').forEach((select) => {
            const componentTitle = select.dataset.componentTitle || 'Component';

            if (!select.value) {
              this.handleErrorMessage(`Please select an option for: ${componentTitle}`);
              hasError = true;
              return;
            }

            const selectedOption = select.options[select.selectedIndex];
            if (selectedOption.dataset.available === 'false') {
              this.handleErrorMessage(`"${componentTitle}" — selected option is out of stock.`);
              hasError = true;
              return;
            }

            bundleItems.push({
              id: parseInt(select.value),
              quantity: 1,
              properties: {
                _bundle_id: bundleId,
                _bundle_role: select.dataset.componentHandle
              }
            });
          });

          // Collect hidden inputs (single-variant components — no selection needed)
          bundleWrapper.querySelectorAll('.bundle-component__variant-input').forEach((input) => {
            bundleItems.push({
              id: parseInt(input.value),
              quantity: 1,
              properties: {
                _bundle_id: bundleId,
                _bundle_role: input.dataset.componentHandle
              }
            });
          });

          // Stop if validation failed
          if (hasError) {
            this.submitButton.removeAttribute('aria-disabled');
            this.submitButton.classList.remove('loading');
            this.querySelector('.loading__spinner').classList.add('hidden');
            return;
          }
        }
        // ===== END BUNDLE COLLECTION =====

        // ===== Determine fetch config based on whether bundle items exist =====
        let fetchPromise;

        if (bundleItems.length > 0) {
          // BUNDLE PATH — send as JSON with multiple items
          const sectionsToRender = this.cart
            ? this.cart.getSectionsToRender().map((section) => section.id)
            : [];

          const jsonBody = JSON.stringify({
            items: bundleItems,
            sections: sectionsToRender,
            sections_url: window.location.pathname
          });

          fetchPromise = fetch(`${routes.cart_add_url}.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: jsonBody
          });

        } else {
          // NATIVE PATH — no bundle components, use original FormData flow
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          const formData = new FormData(this.form);
          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = formData;
          fetchPromise = fetch(`${routes.cart_add_url}`, config);
        }

        // Pull variantId and quantity for pub/sub events (use main item)
        const variantId = bundleItems.length > 0
          ? String(bundleItems[0].id)
          : this.form.querySelector('[name="id"]').value;
        const quantity = bundleItems.length > 0
          ? bundleItems[0].quantity
          : parseInt(this.form.querySelector('[name="quantity"]')?.value || 1);

        if (this.cart) this.cart.setActiveElement(document.activeElement);

        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        // ===== Shared response handling for both paths =====
        fetchPromise
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            // Reset bundle selects after successful add
            if (bundleWrapper) {
              bundleWrapper.querySelectorAll('.bundle-component__variant-select').forEach((select) => {
                select.value = '';
              });
              const totalEl = document.getElementById('bundle-total-price');
              if (totalEl) totalEl.textContent = '₹0.00'; // adjust currency symbol if needed
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });

            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}


/* ===== BUNDLE COMPONENT PICKERS ===== */
(function () {
  const wrapper = document.getElementById('bundle-components');
  if (!wrapper) return;

  const totalPriceEl = document.getElementById('bundle-total-price');

  function formatMoney(cents) {
    return (cents / 100).toLocaleString('en-IN', {
      style: 'currency',
      currency: 'INR',       // ← change to your store currency
      minimumFractionDigits: 2
    });
  }

  function recalculateTotal() {
    let total = 0;

    // Count prices from <select> elements
    wrapper.querySelectorAll('.bundle-component__variant-select').forEach((select) => {
      const selected = select.options[select.selectedIndex];
      if (selected && selected.dataset.price) {
        total += parseInt(selected.dataset.price, 10);
      }
    });

    // Count prices from hidden inputs (single-variant components)
    // These don't have a price data attr, so skip for now
    // (you can add data-price to hidden inputs in Liquid above if needed)

    if (totalPriceEl) {
      totalPriceEl.textContent = formatMoney(total);
    }
  }

  function updateAvailability(select) {
    const handle = select.dataset.componentHandle;
    const feedbackEl = document.getElementById(`bundle-availability-${handle}`);
    if (!feedbackEl) return;

    const selected = select.options[select.selectedIndex];
    if (!selected || !selected.value) {
      feedbackEl.textContent = '';
      return;
    }

    const isAvailable = selected.dataset.available === 'true';
    feedbackEl.textContent = isAvailable ? '● In stock' : '● Out of stock';
    feedbackEl.style.color = isAvailable ? '#2a7a2a' : '#c0392b';
  }

  // Attach change listeners to all selects
  wrapper.querySelectorAll('.bundle-component__variant-select').forEach((select) => {
    select.addEventListener('change', () => {
      updateAvailability(select);
      recalculateTotal();
    });
  });

  // Run once on load to initialize total (all at 0 since nothing selected yet)
  recalculateTotal();
})();
/* ===== END BUNDLE COMPONENT PICKERS ===== */